import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useDashboardMetrics, RANGE_OPTIONS, type DateRange } from "@/lib/admin/dashboard/metrics";
import { getNewsletterWidgetFn } from "@/lib/newsletter/newsletter.functions";
import { adminNewEnquiryCountFn } from "@/lib/enquiries/enquiries.functions";

export const Route = createFileRoute("/admin/")({
  component: DashboardPage,
});

// Dashboard figures arrive in rupees (major units), matching the Orders page.
const inr = (amount: number) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(
    amount || 0,
  );
const num = (n: number) => new Intl.NumberFormat("en-IN").format(n || 0);

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="border border-black/10 bg-white p-5">
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-black/60">{label}</p>
      <p className="mt-2 text-3xl font-semibold tracking-tight text-black tabular-nums">{value}</p>
      {hint && <p className="mt-1 text-xs text-black/60">{hint}</p>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border border-black/10 bg-white p-6">
      <h3 className="mb-4 border-b border-black/5 pb-2 text-sm font-semibold tracking-tight text-black">
        {title}
      </h3>
      {children}
    </section>
  );
}

function NewEnquiriesKpi() {
  const { data, isError } = useQuery({ queryKey: ["admin", "enquiries", "new-count"], queryFn: () => adminNewEnquiryCountFn() });
  return (
    <Link to="/admin/enquiries" className="block transition hover:opacity-80">
      <Kpi label="New enquiries" value={isError ? "—" : data ? num(data.count) : "…"} hint="Contact page · open Customer Enquiries" />
    </Link>
  );
}

function NewsletterWidget() {
  const { data } = useQuery({
    queryKey: ["newsletter", "widget"],
    queryFn: () => getNewsletterWidgetFn(),
  });
  const cells: [string, string][] = [
    ["Subscribers", num(data?.subscribers ?? 0)],
    ["Sent today", num(data?.sentToday ?? 0)],
    ["Queued", num(data?.queued ?? 0)],
    ["Failed", num(data?.failed ?? 0)],
    ["Open rate", `${data?.openRate ?? 0}%`],
    ["Click rate", `${data?.clickRate ?? 0}%`],
  ];
  return (
    <Card title="Newsletter">
      <div className="grid grid-cols-3 gap-4">
        {cells.map(([label, value]) => (
          <div key={label}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-black/50">{label}</p>
            <p className="mt-1 text-xl text-black tabular-nums">{value}</p>
          </div>
        ))}
      </div>
      <Link
        to="/admin/subscribers"
        className="mt-4 inline-block border border-black/20 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em]"
      >
        Manage subscribers
      </Link>
    </Card>
  );
}

function TrendBars({
  data,
  valueKey,
  format,
}: {
  data: { day: string; revenue: number; orders: number }[];
  valueKey: "revenue" | "orders";
  format: (n: number) => string;
}) {
  const max = Math.max(1, ...data.map((d) => d[valueKey]));
  return (
    <div className="flex h-40 items-end gap-1">
      {data.map((d) => {
        const h = Math.round((d[valueKey] / max) * 100);
        return (
          <div key={d.day} className="flex flex-1 flex-col items-center gap-1" title={`${d.day}: ${format(d[valueKey])}`}>
            <div className="w-full bg-[#C6A15B]/70" style={{ height: `${h}%` }} />
          </div>
        );
      })}
    </div>
  );
}

function DashboardPage() {
  const [range, setRange] = useState<DateRange>("30d");
  const m = useDashboardMetrics(range);
  const { business: b, operational: op, widgets: w, charts } = m;
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-black/50">Business snapshot</p>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight text-black">Command Center</h2>
          <p className="mt-1 text-sm text-black/60">Operational overview across sales, orders and inventory.</p>
        </div>
        <div role="tablist" aria-label="Date range" className="inline-flex border border-black/10 bg-white">
          {RANGE_OPTIONS.map((r) => {
            const active = range === r.key;
            return (
              <button
                key={r.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setRange(r.key)}
                className={
                  "px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] transition " +
                  (active ? "bg-[#C6A15B]/10 text-black" : "text-black/60 hover:text-black")
                }
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Revenue" value={inr(b.revenue)} hint={`${num(b.orders)} orders`} />
        <Kpi label="Orders" value={num(b.orders)} hint={`AOV ${inr(b.aov)}`} />
        <Kpi label="Customers" value={num(b.customers)} hint="Unique buyers" />
        <Kpi label="Products" value={num(b.products)} hint={`${num(b.collections)} collections`} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Inventory value" value={inr(b.inventoryValue)} />
        <Kpi label="Low stock" value={num(op.lowStock)} hint="< 8 units" />
        <Kpi label="Out of stock" value={num(op.outOfStock)} hint="0 units" />
        <Kpi label="Conversion" value={`${b.conversionRate.toFixed(1)}%`} hint="Estimated" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Pending" value={num(op.pending)} />
        <Kpi label="Processing" value={num(op.processing)} />
        <Kpi label="Delivered" value={num(op.delivered)} />
        <Kpi label="Cancelled" value={num(op.cancelled + op.refunded)} hint={`${num(op.refunded)} refunded`} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <NewEnquiriesKpi />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title={`Revenue trend — last ${m.range.days} days`}>
          <TrendBars data={charts.revenueByDay} valueKey="revenue" format={inr} />
        </Card>
        <Card title={`Orders trend — last ${m.range.days} days`}>
          <TrendBars data={charts.revenueByDay} valueKey="orders" format={num} />
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <NewsletterWidget />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Recent orders">
          {w.recentOrders.length === 0 ? (
            <p className="text-sm text-black/60">No orders yet.</p>
          ) : (
            <ul className="divide-y divide-black/10">
              {w.recentOrders.slice(0, 6).map((o) => (
                <li key={o.id} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-black">{o.id}</p>
                    <p className="truncate text-xs text-black/60">
                      {new Date(o.createdAt).toLocaleDateString()} · {o.email}
                    </p>
                  </div>
                  <p className="whitespace-nowrap text-sm text-black tabular-nums">{inr(o.pricing.total)}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Recent activity">
          {w.activity.length === 0 ? (
            <p className="text-sm text-black/60">No activity yet.</p>
          ) : (
            <ul className="divide-y divide-black/10">
              {w.activity.slice(0, 8).map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-4 py-2.5">
                  <p className="truncate text-sm text-black">{a.label}</p>
                  <p className="whitespace-nowrap text-xs text-black/60">
                    {new Date(a.at).toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Top products">
          {w.topProducts.length === 0 ? (
            <p className="text-sm text-black/60">No sales in this range.</p>
          ) : (
            <ul className="divide-y divide-black/10">
              {w.topProducts.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-4 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-black">{p.name}</p>
                    <p className="text-xs text-black/60">{num(p.qty)} sold</p>
                  </div>
                  <p className="whitespace-nowrap text-sm text-black tabular-nums">{inr(p.revenue)}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Top collections">
          {w.topCollections.length === 0 ? (
            <p className="text-sm text-black/60">No collection revenue yet.</p>
          ) : (
            <ul className="divide-y divide-black/10">
              {w.topCollections.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-4 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-black">{c.name}</p>
                    <p className="text-xs text-black/60">{num(c.orders)} orders</p>
                  </div>
                  <p className="whitespace-nowrap text-sm text-black tabular-nums">{inr(c.revenue)}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Low inventory">
          {w.lowInventory.length === 0 ? (
            <p className="text-sm text-black/60">All products above threshold.</p>
          ) : (
            <ul className="divide-y divide-black/10">
              {w.lowInventory.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-4 py-2.5">
                  <p className="truncate text-sm text-black">{p.name}</p>
                  <p
                    className={
                      "whitespace-nowrap text-xs font-semibold uppercase tracking-[0.2em] " +
                      (p.stock === 0 ? "text-red-700" : "text-[#8a6f2e]")
                    }
                  >
                    {p.stock === 0 ? "Out of stock" : `${num(p.stock)} left`}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Latest customers">
          {w.latestCustomers.length === 0 ? (
            <p className="text-sm text-black/60">No customers yet.</p>
          ) : (
            <ul className="divide-y divide-black/10">
              {w.latestCustomers.map((c) => (
                <li key={c.email} className="flex items-center justify-between gap-4 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-black">{c.name || c.email}</p>
                    <p className="truncate text-xs text-black/60">{c.email}</p>
                  </div>
                  <p className="whitespace-nowrap text-sm text-black tabular-nums">{inr(c.spend)}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}